import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { useOpsMonitor } from './Index'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function riskColor(score) {
  if (score >= 0.8) return '#F44336'
  if (score >= 0.6) return '#FF9800'
  if (score >= 0.4) return '#FFC107'
  return '#4CAF50'
}

function riskLabel(score) {
  if (score >= 0.8) return 'CRITICAL'
  if (score >= 0.6) return 'HIGH'
  if (score >= 0.4) return 'MODERATE'
  return 'LOW'
}

const CABIN_COLORS = {
  MINT:       '#1A237E',
  BLUE_EXTRA: '#283593',
  BLUE:       '#3949AB',
  default:    '#607D8B',
}

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

// ─── Risk bar ─────────────────────────────────────────────────────────────────

function RiskBar({ score }) {
  const color = riskColor(score || 0)
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 bg-gray-100 rounded-full h-2 overflow-hidden flex-shrink-0">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.round((score || 0) * 100)}%`, background: color }}
        />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color }}>
        {score != null ? score.toFixed(2) : '—'}
      </span>
      <span className="text-xs font-bold" style={{ color }}>
        {score != null ? riskLabel(score) : ''}
      </span>
    </div>
  )
}

// ─── Action panel ─────────────────────────────────────────────────────────────

function ActionPanel({ pnr, onClose }) {
  const [sending, setSending]     = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [msgText, setMsgText]     = useState('')
  const [result, setResult]       = useState(null)

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

  function sendCloudMist() {
    if (!msgText.trim() || sending) return
    setSending(true)
    fetch('/admin/cloudtraq_gds/message', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body:    JSON.stringify({ pnr_locator: pnr.pnr_locator, body: msgText }),
    })
      .then(r => r.json())
      .then(d => { setResult(d.success ? 'Message sent ✓' : 'Error sending'); setSending(false) })
      .catch(() => { setResult('Error'); setSending(false) })
  }

  function forceAva() {
    if (analyzing) return
    setAnalyzing(true)
    fetch(`/admin/cloudtraq_gds/pnr/${pnr.pnr_locator}/ava`, {
      method:  'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
    })
      .then(r => r.json())
      .then(() => { setResult('AVA analysis triggered ✓'); setAnalyzing(false) })
      .catch(() => { setResult('AVA error'); setAnalyzing(false) })
  }

  return (
    <div className="border-t border-gray-100 pt-3 mt-3 space-y-3">
      {/* CloudMist */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">CloudMist → Passenger</p>
        <div className="flex gap-2">
          <input
            value={msgText}
            onChange={e => setMsgText(e.target.value)}
            placeholder="Message for passenger…"
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
          />
          <button
            onClick={sendCloudMist}
            disabled={sending || !msgText.trim()}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-40"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        <a
          href={`/admin/cloudtraq_gds/pnr/${pnr.pnr_locator}`}
          className="text-xs px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-100"
        >
          View PNR →
        </a>
        {pnr.itinerary_ref && (
          <a
            href={`/admin/cloudtraq_gds/itinerary/${pnr.itinerary_ref}`}
            className="text-xs px-3 py-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded hover:bg-teal-100"
          >
            Full Itinerary →
          </a>
        )}
        <a
          href="/admin/cloudtraq_gds/dispatch_board"
          className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded hover:bg-orange-100"
        >
          Dispatch Board
        </a>
        <button
          onClick={forceAva}
          disabled={analyzing}
          className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 rounded hover:bg-purple-100 disabled:opacity-40"
        >
          {analyzing ? '…' : '🧠 Force AVA'}
        </button>
        <button onClick={onClose} className="text-xs px-3 py-1.5 border border-gray-300 text-gray-500 rounded hover:bg-gray-50">
          Close
        </button>
      </div>

      {result && (
        <p className="text-xs" style={{ color: result.includes('✓') ? '#4CAF50' : '#F44336' }}>{result}</p>
      )}
    </div>
  )
}

// ─── Candidate card ───────────────────────────────────────────────────────────

function CandidateCard({ pnr, live = false, onExpand, expanded }) {
  const risk        = pnr.risk_score || 0
  const color       = riskColor(risk)
  const cabinColor  = CABIN_COLORS[pnr.cabin_class] || CABIN_COLORS.default
  const carrierColor = CARRIER_COLORS[pnr.carrier_code] || '#607D8B'

  return (
    <div
      className={`bg-white rounded-lg border-2 overflow-hidden ${live ? 'border-red-400' : ''}`}
      style={{ borderColor: live ? '#F44336' : (risk >= 0.7 ? color : '#E5E7EB') }}
    >
      {/* Main row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => onExpand(expanded ? null : pnr.pnr_locator)}
      >
        {/* Risk indicator */}
        <div
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
          style={{ background: color }}
        >
          {risk >= 0.7 ? '🔴' : risk >= 0.5 ? '🟠' : '🟡'}
        </div>

        {/* PNR info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-sm text-gray-900">{pnr.pnr_locator}</span>
            {pnr.carrier_code && (
              <span className="font-mono font-bold text-xs" style={{ color: carrierColor }}>
                {pnr.carrier_code}
              </span>
            )}
            {pnr.cabin_class && (
              <span
                className="text-xs px-1.5 py-0 rounded font-bold"
                style={{ background: cabinColor + '22', color: cabinColor }}
              >
                {pnr.cabin_class}
              </span>
            )}
            {live && (
              <span className="text-xs font-bold text-red-500 animate-pulse">⚡ HIGH RISK ALERT</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {pnr.current_anchor && <span>{pnr.current_anchor} · </span>}
            {pnr.conflict_types?.length > 0 ? pnr.conflict_types.join(', ') : 'No conflicts logged'}
          </p>
        </div>

        {/* Risk bar */}
        <div className="flex-shrink-0">
          <RiskBar score={pnr.risk_score} />
        </div>

        <button className="text-gray-400 text-sm flex-shrink-0">{expanded ? '▲' : '▼'}</button>
      </div>

      {/* Expanded action panel */}
      {expanded && (
        <div className="px-4 pb-4">
          <ActionPanel pnr={pnr} onClose={() => onExpand(null)} />
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HighRisk({ candidates, tenant_branding }) {
  const intervalRef                 = useRef(null)
  const [rows, setRows]             = useState(candidates)
  const [expanded, setExpanded]     = useState(null)
  const [liveAlerts, setLiveAlerts] = useState([])

  // Sync on prop change
  useEffect(() => { setRows(candidates) }, [candidates])

  // Auto-reload every 30s
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['candidates'] })
    }, 30_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  // Real-time HIGH_RISK_ALERT: inject at top of list
  useOpsMonitor({
    tenantKey: tenant_branding?.tenant_key || 'cloudtraq',
    onEvent: (event, payload) => {
      if (event !== 'HIGH_RISK_ALERT' || !payload) return
      setLiveAlerts(prev => {
        const already = prev.find(a => a.pnr_locator === payload.pnr_locator)
        if (already) return prev
        return [
          {
            pnr_locator: payload.pnr_locator,
            risk_score:  payload.risk_score,
            carrier_code: payload.carrier_code,
            conflict_types: ['ava_high_risk'],
            _live: true,
          },
          ...prev,
        ].slice(0, 20)
      })
    },
  })

  // Merge live + server rows (live first, no duplicates)
  const mergedRows = [
    ...liveAlerts.filter(a => !rows.find(r => r.pnr_locator === a.pnr_locator)),
    ...rows,
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">High Risk Candidates</h1>
            <p className="text-sm text-gray-500 mt-1">
              riskScore &gt; 0.6 · active PNRs ·{' '}
              {mergedRows.length} candidates
              {liveAlerts.length > 0 && (
                <span className="ml-1.5 text-red-600 font-semibold">· {liveAlerts.length} live alerts</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload({ only: ['candidates'] })} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-4 mb-4 flex-wrap text-xs">
          {[
            { label: 'Critical (≥0.8)', color: '#F44336' },
            { label: 'High (≥0.6)',     color: '#FF9800' },
            { label: 'Moderate (≥0.4)', color: '#FFC107' },
          ].map(({ label, color }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
              <span className="text-gray-600">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-4">
            <span className="text-red-500 font-bold text-xs animate-pulse">⚡</span>
            <span className="text-gray-600">Live HIGH_RISK_ALERT (OpsMonitorChannel)</span>
          </div>
        </div>

        {/* Candidate list */}
        {mergedRows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">🎉 No high-risk candidates — all PNRs below threshold.</p>
            <p className="text-gray-400 text-xs mt-1">
              Candidates appear when riskScore &gt; 0.6 from AvaInsightsController.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {mergedRows.map(pnr => (
              <CandidateCard
                key={pnr.pnr_locator}
                pnr={pnr}
                live={!!pnr._live}
                expanded={expanded === pnr.pnr_locator}
                onExpand={setExpanded}
              />
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/dispatch_board" className="text-blue-600 hover:underline">Dispatch Board</Link>
          <Link href="/admin/cloudtraq_gds/channel_sensor" className="text-blue-600 hover:underline">channelSensor Ops</Link>
        </div>
      </div>
    </div>
  )
}
